/**
 * ColorFilter - Composite component for WUBRG + Multi/Colorless filtering
 *
 * Renders a cohesive row of color filter buttons using mana-font icons.
 * Supports optional Multi (5-color wheel) and Colorless (diamond) buttons.
 */
import type { ReactElement } from 'react';

import type { Color } from '@/types/card';
import type { SpecialColor } from '@/types/schemas';

import ColorFilterButton, { type SpecialColorFilter } from '../ColorFilterButton/ColorFilterButton';

const COLORS: Color[] = ['W', 'U', 'B', 'R', 'G'];
const SPECIAL_COLORS: SpecialColor[] = ['multicolor', 'colorless'];

/** Map schema SpecialColor to ColorFilterButton's SpecialColorFilter */
const SPECIAL_COLOR_MAP: Record<SpecialColor, SpecialColorFilter> = {
  multicolor: 'multi',
  colorless: 'colorless',
};

export interface ColorFilterProps {
  /** Currently selected WUBRG colors */
  colors: Color[];
  /** Currently selected special colors (multicolor/colorless) */
  specialColors?: SpecialColor[];
  /** Callback when a WUBRG color is toggled */
  onColorToggle: (color: Color) => void;
  /** Callback when a special color is toggled */
  onSpecialColorToggle?: (special: SpecialColor) => void;
  /** Whether to include Multi/Colorless buttons (default: true) */
  includeMultiColorless?: boolean;
}

export default function ColorFilter({
  colors,
  specialColors = [],
  onColorToggle,
  onSpecialColorToggle,
  includeMultiColorless = true,
}: ColorFilterProps): ReactElement {
  return (
    <div className="flex items-center gap-1">
      {/* WUBRG color buttons */}
      {COLORS.map((color) => (
        <ColorFilterButton key={color} color={color} isActive={colors.includes(color)} onClick={() => onColorToggle(color)} />
      ))}

      {/* Multi/Colorless buttons (optional) */}
      {includeMultiColorless &&
        onSpecialColorToggle &&
        SPECIAL_COLORS.map((sc) => (
          <ColorFilterButton
            key={sc}
            color={SPECIAL_COLOR_MAP[sc]}
            isActive={specialColors.includes(sc)}
            onClick={() => onSpecialColorToggle(sc)}
          />
        ))}
    </div>
  );
}
